<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { PropType, Ref } from 'vue'
import { deleteLifeArticle, type DeleteLifeArticleResponse, type LifeArticleDTO } from '@/composables/memorize/memorize'
import { handleErr } from '@/composables/exception/handler'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import { ElMessage, ElMessageBox } from 'element-plus'
import { renderMessage } from '@/utils/util'
import { closeLoading, openLoading } from '../loading/loading'
import { useI18n } from 'vue-i18n'
import CoworkSignature from './CoworkSignature.vue'
import { currentUserHandle } from '@/composables/user/users'

const { t } = useI18n()

const props = defineProps({
  article: {
    type: Object as PropType<LifeArticleDTO>,
    required: true
  },
  anim: {
    type: Boolean,
    default: false,
    required: false
  },
  canManage: {
    type: Boolean,
    default: true,
    required: false
  },
  canEdit: {
    type: Boolean,
    default: true,
    required: false
  }
})

const reverse = ref(false)
const moreVisible = ref(false)
let moreRef = ref(null)

const emit = defineEmits(['onDelete', 'animationEnd', 'onEdit', 'onShare'])

const articleRef = computed(() => props.article)

const canDelete = computed(() => {
  console.log('props.canManage', props.canManage)
  return props.canManage
})

const canEdit = computed(() => {
  console.log('props.canEdit', props.canEdit, articleRef.value.creator.id === currentUserHandle.value)
  return props.canEdit || articleRef.value.creator.id === currentUserHandle.value
})

function judgeDelete() {
  moreVisible.value = false
  ElMessageBox.confirm(t('m.confirm_delete_life_article'), {
    confirmButtonText: t('action.confirm'),
    cancelButtonText: t('action.cancel'),
    type: 'warning'
  })
    .then(() => {
      toDelete()
    })
    .catch(() => {})
}

async function toDelete() {
  moreVisible.value = false
  openLoading(t('alt.loading'))
  const { data, error, statusCode } = await deleteLifeArticle(articleRef.value.id)
  closeLoading()
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<DeleteLifeArticleResponse>>
    if (resData.value.code === CODE_OK) {
      // success
      const response: DeleteLifeArticleResponse = resData.value.data
      emit('onDelete', response.lifeArticleId)
      ElMessage({
        message: renderMessage(t('common.delete_success')),
        type: 'success'
      })
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

function toEdit() {
  moreVisible.value = false
  emit('onEdit')
}

function toForward() {
  moreVisible.value = false
  emit('onShare')
}

const animPlayed = ref(false)
watch(
  () => props.anim,
  (val) => {
    animPlayed.value = val
    setTimeout(() => {
      emit('animationEnd')
      console.log('animationEnd')
    }, 1500)
  }
)
</script>
<template>
  <article
    w-full
    :class="animPlayed ? 'animated-background' : ''"
    flex="~ col"
    bg-theme-light
    rounded-3
    mt-3
    px-4
    py-4
    md:px-6>
    <Article :modelValue="articleRef.content" :max-height="600" :enableTheme="true" mb-2 />
    <div flex="~ row" mt-1.5 mb-1 w-full items-center justify-end>
      <CoworkSignature
        :creatorName="articleRef.creator.name"
        :createTime="articleRef.createdAt"
        :creatorRelation="articleRef?.creatorRelation"
        :updaterName="articleRef?.updater?.name || null"
        :updateTime="articleRef?.updatedAt || 0"
        :updaterRelation="articleRef?.updaterRelation || null"
        mr-2 />
      <div ref="moreRef">
        <ClientOnly>
          <RIcon
            v-if="canEdit || canDelete"
            icon="i-iconamoon:menu-kebab-horizontal-fill"
            :theme="true"
            :reverse="reverse"
            size="medium"
            @click.prevent.stop="moreVisible = !moreVisible"></RIcon>
        </ClientOnly>
        <RToolTip :wrapper-ref="moreRef" placement="left" :arrow="false" v-model="moreVisible">
          <div flex flex-row items-center text-theme-dark>
            <ClientOnly>
              <div v-if="canDelete" flex="~ row" items-center quick-menu-item @click="judgeDelete" text-danger>
                <i i-mi:delete></i>
                <p px-2>{{ $t('action.delete') }}</p>
              </div>
            </ClientOnly>
            <ClientOnly>
              <div v-if="canEdit" flex="~ row" items-center quick-menu-item @click="toEdit">
                <i i-ri:edit-line text-base></i>
                <p px-2>{{ $t('action.edit') }}</p>
              </div>
            </ClientOnly>
            <!-- <div flex="~ row" items-center quick-menu-item @click="toForward">
              <i i-majesticons:share-line text-base></i>
              <p px-2>{{ $t('action.forward') }}</p>
            </div> -->
          </div>
        </RToolTip>
      </div>
    </div>
  </article>
</template>
<style lang="scss" scoped></style>
